class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Flag to track if inventory check has been run for this drawer session
    this.inventoryCheckRun = false;
    this._inventoryDebounceTimer = null;
    this._recommendationsPrefetchPending = false;
    this._boundQuantityButtonClick = this.handleQuantityButtonClick.bind(this);
    this._boundInventoryUpdated = () => {
      if (this.classList.contains('active') && !this.inventoryCheckRun) {
        setTimeout(async () => {
          await this.renderRecommendationsWithInventory();
        }, 100);
      }
    };
    this._boundDeliveryModalBrowse = () => {
      if (this.classList.contains('active') && !this.inventoryCheckRun) {
        setTimeout(async () => {
          await this.renderRecommendationsWithInventory();
        }, 500);
      }
    };
    this._boundDateChanged = () => {
      if (this.classList.contains('active') && !this.inventoryCheckRun) {
        setTimeout(async () => {
          await this.renderRecommendationsWithInventory();
        }, 300);
      }
      this.updateButtonStates();
    };
    this._boundStorage = (event) => {
      if (!event || !event.key || event.key.startsWith('selected_') || event.key === 'et_fulfillment_committed') {
        this.refreshDrawerInventoryUi();
      }
    };
    this.refreshDrawerInventoryUi = () => {
      this.updateButtonStates();
      if (this.classList.contains('active')) {
        if (this._inventoryDebounceTimer) clearTimeout(this._inventoryDebounceTimer);
        this._inventoryDebounceTimer = setTimeout(async () => {
          this._inventoryDebounceTimer = null;
          try {
            await this.renderRecommendationsWithInventory();
          } finally {
            this.inventoryCheckRun = false;
          }
        }, 220);
      }
    };

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // Headless-browser snapshot libraries clone this custom element before its
    // children are attached, so the overlay can be null. Bail out rather than
    // deref null — there's no live drawer to wire up on a childless clone.
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;
    overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initQuantityButtons();
    this.initRecommendationButtons();
    this.initRecommendationsScroll();
    this.initSubscriptionToggle();
    
    // Listen for inventory changes that might affect recommendations
    document.addEventListener('inventoryUpdated', this._boundInventoryUpdated);
    
    // Listen for delivery modal changes that affect inventory
    document.addEventListener('deliveryModal:browseMeals', this._boundDeliveryModalBrowse);
    
    // Listen for specific date and location changes
    document.addEventListener('dateChanged', this._boundDateChanged);
    
    document.addEventListener('fulfillmentDateChanged', this.refreshDrawerInventoryUi);
    document.addEventListener('locationChanged', this.refreshDrawerInventoryUi);
    document.addEventListener('fulfillmentCommitted', this.refreshDrawerInventoryUi);
    window.addEventListener('storage', this._boundStorage);
  }

  disconnectedCallback() {
    if (this._boundInventoryUpdated) {
      document.removeEventListener('inventoryUpdated', this._boundInventoryUpdated);
    }
    if (this._boundDeliveryModalBrowse) {
      document.removeEventListener('deliveryModal:browseMeals', this._boundDeliveryModalBrowse);
    }
    if (this._boundDateChanged) {
      document.removeEventListener('dateChanged', this._boundDateChanged);
    }
    if (this._boundStorage) {
      window.removeEventListener('storage', this._boundStorage);
    }
    if (this.refreshDrawerInventoryUi) {
      document.removeEventListener('fulfillmentDateChanged', this.refreshDrawerInventoryUi);
      document.removeEventListener('locationChanged', this.refreshDrawerInventoryUi);
      document.removeEventListener('fulfillmentCommitted', this.refreshDrawerInventoryUi);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  initQuantityButtons() {
    // Usamos delegación de eventos para capturar los clics en botones actuales y futuros
    this.removeEventListener('click', this._boundQuantityButtonClick);
    this.addEventListener('click', this._boundQuantityButtonClick);
    
    // Añadir efecto hover para mejor feedback
    const quantityButtons = this.querySelectorAll('.cart-drawer__quantity-button, .cart-drawer__quantity-remove');
    quantityButtons.forEach(button => {
      button.addEventListener('mouseenter', function() {
        if (!this.classList.contains('disabled')) {
          this.style.opacity = '0.8';
        }
      });
      button.addEventListener('mouseleave', function() {
        if (!this.classList.contains('disabled')) {
          this.style.opacity = '1';
        }
      });
    });
    
    // Actualizar el estado de los botones
    this.updateButtonStates();
  }

  initRecommendationButtons() {
    const recommendationButtons = this.querySelectorAll(".cart-drawer__add-recommendation");
    recommendationButtons.forEach((button) => {
      button.addEventListener("click", this.handleRecommendationAdd.bind(this));
    });
  }

  initSubscriptionToggle() {
    const subscriptionCheckbox = this.querySelector('.cart-drawer__subscription-checkbox');
    if (subscriptionCheckbox) {
      // Restablecer estado guardado si existe
      const savedState = localStorage.getItem('cartSubscriptionEnabled');
      if (savedState === 'true') {
        subscriptionCheckbox.checked = true;
      }
      
      subscriptionCheckbox.addEventListener('change', (event) => {
        const isChecked = event.target.checked;
        
        if (isChecked) {
          // Si el toggle se activa, mostrar el popup
          this.showSubscriptionModal();
        } else {
          // Si el toggle se desactiva, actualizar el estado
          localStorage.setItem('cartSubscriptionEnabled', 'false');
          this.handleSubscriptionChange(false);
        }
      });
    }
  }
  
  showSubscriptionModal() {
    const modal = document.getElementById('SubscriptionModal');
    if (!modal) return;
    
    // Mostrar el modal
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('active');
    document.body.classList.add('overflow-hidden');
    
    // Configurar event listeners
    const closeButton = modal.querySelector('.subscription-modal__close');
    const cancelButton = modal.querySelector('.subscription-modal__button--cancel');
    const createButton = modal.querySelector('.subscription-modal__button--create');
    const overlay = modal.querySelector('.subscription-modal__overlay');
    
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closeSubscriptionModal(false));
    }
    
    if (cancelButton) {
      cancelButton.addEventListener('click', () => this.closeSubscriptionModal(false));
    }
    
    if (createButton) {
      createButton.addEventListener('click', () => this.closeSubscriptionModal(true));
    }
    
    if (overlay) {
      overlay.addEventListener('click', () => this.closeSubscriptionModal(false));
    }
    
    // Cerrar el modal al presionar Escape
    document.addEventListener('keydown', this.handleSubscriptionModalKeydown);
  }
  
  closeSubscriptionModal(confirmed) {
    const modal = document.getElementById('SubscriptionModal');
    if (!modal) return;
    
    // Remover la clase active para ocultar el modal
    modal.classList.remove('active');
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    
    // Eliminar event listeners
    document.removeEventListener('keydown', this.handleSubscriptionModalKeydown);
    
    // Actualizar el checkbox y localStorage según el resultado
    const subscriptionCheckbox = this.querySelector('.cart-drawer__subscription-checkbox');
    if (subscriptionCheckbox) {
      if (!confirmed) {
        // Si canceló, desmarcar el checkbox
        subscriptionCheckbox.checked = false;
      } else {
        // Si confirmó, guardar el estado
        localStorage.setItem('cartSubscriptionEnabled', 'true');
        this.handleSubscriptionChange(true);
      }
    }
  }
  
  handleSubscriptionModalKeydown = (event) => {
    if (event.key === 'Escape') {
      this.closeSubscriptionModal(false);
    }
  }

  async handlePrefillSubscriptionProducts() {
    const redirectUrl = '/pages/subscriptions';
    if (!localStorage.getItem('selectedSubscriptionPlan')) {
      localStorage.setItem('selectedSubscriptionPlan', '1-week');
    }
    
    try {
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        
        // Get the selected subscription plan from localStorage
        const selectedPlan = localStorage.getItem('selectedSubscriptionPlan') || '1-week';
        
        const mapCartItem = (item, sellingPlanId = null) => ({
          variantId: item.variant_id.toString(),
          productId: item.product_id.toString(),
          title: item.product_title,
          image: item.image,
          qty: item.quantity,
          sellingPlanId: sellingPlanId
        });

        const rechargeSidebarCart = await Promise.all(cart.items.map(async (item) => {
          // Skip operational fee products - delivery and service fees
          const packagingFeeProductId = (this.dataset.packagingFeeProductId || '').trim();
          const packagingFeeVariantId = (this.dataset.packagingFeeVariantId || '').trim();
          const deliveryFeeProductId = (this.dataset.deliveryFeeProductId || '').trim();
          const deliveryFeeVariantId = (this.dataset.deliveryFeeVariantId || '').trim();

          const variantId = item?.variant_id != null ? String(item.variant_id) : '';
          const productId = item?.product_id != null ? String(item.product_id) : '';

          if (
            (packagingFeeProductId && productId === packagingFeeProductId) ||
            (packagingFeeVariantId && variantId === packagingFeeVariantId) ||
            (deliveryFeeProductId && productId === deliveryFeeProductId) ||
            (deliveryFeeVariantId && variantId === deliveryFeeVariantId)
          ) {
            return null;
          }
          
          let sellingPlanId = null;
          
          try {
            if (!item.handle) {
              return mapCartItem(item, null);
            }
            
            const productResponse = await fetch(`/products/${item.handle}.js`);
            if (!productResponse.ok) {
              return mapCartItem(item, null);
            }
            
            const product = await productResponse.json();
            
            try {
              if (product && product.selling_plan_groups && Array.isArray(product.selling_plan_groups) && product.selling_plan_groups.length > 0) {
                for (const group of product.selling_plan_groups) {
                  if (group?.options?.length) {
                    const frequencyOption = group.options.find(opt => 
                      opt?.name === 'Order Frequency and Unit' && 
                      opt?.values?.length > 0
                    );
                    
                    if (frequencyOption?.values[0] === selectedPlan) {
                      if (group?.selling_plans?.length > 0) {
                        const firstSellingPlan = group.selling_plans[0];
                        if (firstSellingPlan?.id) {
                          sellingPlanId = firstSellingPlan.id;
                          break;
                        }
                      }
                    }
                  }
                }
              }
            } catch (groupError) {
              console.error(`Error parsing selling plan groups for product ${item.product_id}:`, groupError);
            }
          } catch (error) {
            console.error(`Error fetching product data for ${item.handle}:`, error);
          }
          
          return mapCartItem(item, sellingPlanId);
        }));

        // Filter out null values (excluded products)
        const filteredRechargeSidebarCart = rechargeSidebarCart.filter(item => item !== null);
        
        localStorage.setItem('rechargeSidebarCart', JSON.stringify(filteredRechargeSidebarCart));
        
        // Clear cart and reset subscription state before redirecting
        await this.clearCartAndResetSubscription();
        
        window.location.href = redirectUrl;
      } else {
        console.error('Failed to fetch cart data');
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error('Error fetching cart data:', error);
      window.location.href = redirectUrl;
    }
  }

  async clearCartAndResetSubscription() {
    try {
      // Clear the cart
      const clearCartResponse = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (clearCartResponse.ok) {
      } else {
      }
    } catch (error) {
      console.error('Error clearing cart before redirecting:', error);
    }
    
    // Reset subscription toggle state
    const subscriptionCheckbox = this.querySelector('.cart-drawer__subscription-checkbox');
    if (subscriptionCheckbox) {
      subscriptionCheckbox.checked = false;
    }
    localStorage.setItem('cartSubscriptionEnabled', 'false');
  }

  handleSubscriptionChange(isEnabled) {
    if (isEnabled) {
      const checkoutButton = document.getElementById('CartDrawer-Checkout');
      if (checkoutButton) {
        const subtotalElement = checkoutButton.querySelector('.cart-drawer__checkout-subtotal');
        if (subtotalElement) {
          const originalSubtotal = subtotalElement.textContent.match(/[\d,.]+/);
          if (originalSubtotal && originalSubtotal[0]) {
            const subtotalValue = parseFloat(originalSubtotal[0].replace(/,/g, ''));
            const discountedValue = subtotalValue * 0.9;
            
            const formattedValue = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2
            }).format(discountedValue);
            
            subtotalElement.innerHTML = `Subtotal: <span class="cart-drawer__checkout-discount">${formattedValue}</span> <span class="cart-drawer__checkout-original">${originalSubtotal[0]}</span>`;
          }
        }
      }
      
      this.handlePrefillSubscriptionProducts();
    } else {
      // Restaurar el estado original
      const checkoutButton = document.getElementById('CartDrawer-Checkout');
      if (checkoutButton) {
        const subtotalElement = checkoutButton.querySelector('.cart-drawer__checkout-subtotal');
        if (subtotalElement) {
          // Restaurar el subtotal original
          const originalSubtotal = subtotalElement.querySelector('.cart-drawer__checkout-original');
          if (originalSubtotal) {
            subtotalElement.textContent = `Subtotal: ${originalSubtotal.textContent}`;
          }
        }
      }
    }
  }

  initRecommendationsScroll() {
    // Solo aplicar scroll horizontal a la sección de recomendaciones
    const scrollContainer = this.querySelector('.cart-drawer__recommendations-scroll');
    if (scrollContainer) {
      // Permitir arrastrar para desplazarse en dispositivos móviles y de escritorio
      let isDown = false;
      let startX;
      let scrollLeft;

      scrollContainer.addEventListener('mousedown', (e) => {
        isDown = true;
        scrollContainer.style.cursor = 'grabbing';
        startX = e.pageX - scrollContainer.offsetLeft;
        scrollLeft = scrollContainer.scrollLeft;
      });

      scrollContainer.addEventListener('mouseleave', () => {
        isDown = false;
        scrollContainer.style.cursor = 'grab';
      });

      scrollContainer.addEventListener('mouseup', () => {
        isDown = false;
        scrollContainer.style.cursor = 'grab';
      });

      scrollContainer.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - scrollContainer.offsetLeft;
        const walk = (x - startX) * 2; // Velocidad del scroll
        scrollContainer.scrollLeft = scrollLeft - walk;
      });
    }
  }

  async handleQuantityButtonClick(event) {
    const button = event.target.closest('.cart-drawer__quantity-button') || event.target.closest('.cart-drawer__quantity-remove');
    if (!button) return;

    const isMinusButton = button.classList.contains('cart-drawer__quantity-minus');
    const isPlusButton = button.classList.contains('cart-drawer__quantity-plus');
    const isRemoveButton = button.classList.contains('cart-drawer__quantity-remove');
    
    if (!isMinusButton && !isPlusButton && !isRemoveButton) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    if (button.classList.contains('processing')) {
      return;
    }
    
    // Añadir efecto de clic
    button.classList.add('processing');
    button.style.transform = 'scale(0.95)';
    
    // Reemplazar el contenido del botón con un indicador de carga si es necesario
    const originalContent = button.innerHTML;
    
    // Procesamos la acción de inmediato para mejor respuesta
    let index = 0;
    if (isPlusButton) {
      index = button.dataset.index;
      if (!(await this.checkInventoryBeforeIncrement(index))) {
        // Restore button state and return early
        setTimeout(() => {
          button.style.transform = '';
          button.classList.remove('processing');
          if (button.innerHTML !== originalContent) {
            button.innerHTML = originalContent;
          }
        }, 300);
        return;
      }
      this.updateItemQuantity(index, 1);
    } else if (isMinusButton) {
      index = button.dataset.index;
      
      // Verificamos si es cantidad 1 antes de procesar
      const item = Array.from(this.querySelectorAll('.cart-drawer__item'))[index - 1];
      const quantityElement = item ? item.querySelector('.cart-drawer__quantity-number') : null;
      const currentQty = quantityElement ? parseInt(quantityElement.textContent) : 0;
      
      // Si la cantidad es 1 y se pulsa -, eliminar el producto
      if (currentQty === 1) {
        this.updateItemQuantity(index, 0, true);
      } else {
        this.updateItemQuantity(index, -1);
      }
    } else if (isRemoveButton) {
      index = button.dataset.index;
      this.updateItemQuantity(index, 0, true);
    }
    
    // Restauramos el botón después de un tiempo corto
    setTimeout(() => {
      button.style.transform = '';
      button.classList.remove('processing');
      if (button.innerHTML !== originalContent) {
        button.innerHTML = originalContent;
      }
    }, 300);
  }

  updateItemQuantity(lineIndex, change, isRemove = false) {
    
    const item = Array.from(this.querySelectorAll('.cart-drawer__item'))[lineIndex - 1];
    if (!item) {
      console.error('❌ Cart Drawer: Could not find cart item with index:', lineIndex);
      return;
    }

    const variantId = item.dataset.variantId;
    const quantityElement = item.querySelector('.cart-drawer__quantity-number');
    const currentQty = quantityElement ? parseInt(quantityElement.textContent) : 0;
    const newQty = isRemove ? 0 : Math.max(0, currentQty + change);
    // Add updating class for visual feedback
    item.classList.add('updating');
    item.style.opacity = '0.7';
    item.style.transform = 'scale(0.98)';

    const data = {
      id: variantId,
      quantity: newQty,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname
    };

    fetch(`${routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error('❌ Cart Drawer: Error updating quantity:', response.description);
          // Revert visual changes on error
          item.classList.remove('updating');
          item.style.opacity = '';
          item.style.transform = '';
          return;
        }

        this.renderContents(response);
        
        // Publish cart update event to trigger inventory updates
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'cart-drawer', 
          cartData: response, 
          variantId: variantId 
        });
      })
      .catch((error) => {
        console.error('❌ Cart Drawer: Network error updating quantity:', error);
        // Revert visual changes on error
        item.classList.remove('updating');
        item.style.opacity = '';
        item.style.transform = '';
      });
  }

  handleRecommendationAdd(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    
    // Prevent action if button is disabled (out of stock)
    if (button.classList.contains("loading") || button.disabled || button.classList.contains("disabled")) {
      return;
    }
    
    // Check inventory before adding
    if (this.isTodayInventoryGuardActive()) {
      const productsData = this.getCachedInventoryProducts();
      if (!productsData) {
        if (window.inventoryValidator && typeof window.inventoryValidator.ensureInventoryPrefetch === 'function') {
          window.inventoryValidator.ensureInventoryPrefetch().catch(() => {});
        }
        this.showRecommendationInventoryWarning(button, 0);
        return;
      }

      const inventoryInfo = this.getInventoryInfoForCartItem(
        {
          dataset: {
            variantId: String(variantId || ''),
            productId: String(productId || ''),
            isBundle: button.dataset.isBundle || 'false'
          }
        },
        productsData
      );
      const availableQuantity = inventoryInfo ? Math.max(0, Number(inventoryInfo.available) || 0) : 0;

      const currentCartItem = Array.from(this.querySelectorAll('.cart-drawer__item')).find(
        (itemEl) => String(itemEl.dataset.variantId || '') === String(variantId || '')
      );
      const currentQtyEl = currentCartItem ? currentCartItem.querySelector('.cart-drawer__quantity-number') : null;
      const currentQty = currentQtyEl ? parseInt(currentQtyEl.textContent) || 0 : 0;

      if (availableQuantity <= 0 || currentQty >= availableQuantity) {
        this.showRecommendationInventoryWarning(button, availableQuantity);
        return;
      }
    }
    
    button.classList.add("loading");
    button.innerHTML = '<span class="cart-drawer__add-recommendation-icon">...</span>';
    
    const data = {
      id: variantId,
      quantity: 1,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname
    };

    const syncPromise = typeof syncAllAttributesToCart === 'function'
      ? Promise.resolve(syncAllAttributesToCart()).catch(() => {})
      : Promise.resolve();
    syncPromise.then(() => {
      return fetch(`${routes.cart_add_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(data)
      });
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          button.classList.remove("loading");
          button.classList.add("error");
          button.innerHTML = '<span class="cart-drawer__add-recommendation-icon">!</span>';
          setTimeout(() => {
            button.classList.remove("error");
            button.innerHTML = '<span class="cart-drawer__add-recommendation-icon">+</span>';
          }, 2000);
          return;
        }

        button.classList.remove("loading");
        button.classList.add("success");
        button.innerHTML = '<span class="cart-drawer__add-recommendation-icon">✓</span>';
        
        setTimeout(() => {
          button.classList.remove("success");
          button.innerHTML = '<span class="cart-drawer__add-recommendation-icon">+</span>';
        }, 2000);
        
        this.renderContents(response);
        
        // Reset inventory check flag to allow for future updates after adding item
        this.inventoryCheckRun = false;
      })
      .catch((error) => {
        button.classList.remove("loading");
        button.classList.add("error");
        button.innerHTML = '<span class="cart-drawer__add-recommendation-icon">!</span>';
        setTimeout(() => {
          button.classList.remove("error");
          button.innerHTML = '<span class="cart-drawer__add-recommendation-icon">+</span>';
        }, 2000);
        console.error("Error:", error);
      });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    
    // Run inventory check only once when drawer opens
    setTimeout(async () => {
      await this.renderRecommendationsWithInventory();
    }, 100);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    
    // Reset inventory check flag for next drawer session
    this.inventoryCheckRun = false;
    
    // Reset all recommendation items to hidden state when closing
    const recommendationsContainer = this.querySelector('.cart-drawer__recommendations-items');
    if (recommendationsContainer) {
      const recommendationItems = recommendationsContainer.querySelectorAll('.cart-drawer__recommendation-item');
      recommendationItems.forEach(item => {
        item.style.display = 'none';
        item.style.opacity = '0';
        item.style.transform = 'translateY(10px)';
        item.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      });
    }
    
    // Remove any empty state messages
    const recommendationsSectionClose = this.querySelector('.cart-drawer__recommendations');
    if (recommendationsSectionClose) {
      const emptyMessage = recommendationsSectionClose.querySelector('.cart-drawer__recommendations-empty');
      if (emptyMessage) {
        emptyMessage.remove();
      }
    }
    
    // Also clear any empty messages
    const recommendationsSection = this.querySelector('.cart-drawer__recommendations');
    if (recommendationsSection) {
      const emptyMessage = recommendationsSection.querySelector('.cart-drawer__recommendations-empty');
      if (emptyMessage) {
        emptyMessage.remove();
      }
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      // Same null-overlay guard as the constructor: the re-rendered section
      // markup may not contain #CartDrawer-Overlay, so don't deref null.
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      // Re-initialize all the event handlers after DOM update
      this.initQuantityButtons();
      this.initRecommendationButtons();
      this.initRecommendationsScroll();
      this.initSubscriptionToggle();
      
      // Update button states including inventory checks
      this.updateButtonStates();
      
      // Quitar cualquier clase updating que pudiera haber quedado
      this.querySelectorAll('.cart-drawer__item.updating').forEach(item => {
        item.classList.remove('updating');
        item.style.opacity = '';
        item.style.transform = '';
      });
      
      // Re-render recommendations to update "+" button states based on new cart quantities
      if (this.classList.contains('active')) {
        // Reset inventory check flag to allow re-rendering
        this.inventoryCheckRun = false;
        setTimeout(async () => {
          await this.renderRecommendationsWithInventory();
        }, 200); // Small delay to ensure cart data is updated
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  normalizeProductId(productId) {
    if (window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeProductId === 'function') {
      return window.EtInventoryDomain.normalizeProductId(productId);
    }
    const raw = (productId || '').toString().trim();
    if (!raw) return '';
    if (raw.includes('gid://shopify/Product/')) {
      return raw.split('/').pop() || raw;
    }
    return raw;
  }

  getSelectedLocationIdFromStorage() {
    const fulfillmentConstants = window.FulfillmentConstants;
    const locationKey = fulfillmentConstants && fulfillmentConstants.LOCATION_ID ? fulfillmentConstants.LOCATION_ID : 'selected_location_id';
    const rawLocationId = localStorage.getItem(locationKey);
    if (!rawLocationId) return '';
    if (window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeLocationId === 'function') {
      return window.EtInventoryDomain.normalizeLocationId(rawLocationId);
    }
    const raw = String(rawLocationId).trim();
    if (!raw) return '';
    if (raw.includes('gid://shopify/Location/')) {
      return raw.split('/').pop() || raw;
    }
    return raw;
  }

  isSelectedFulfillmentDateToday() {
    if (window.inventoryValidator && typeof window.inventoryValidator.isTodaySelected === 'function') {
      return !!window.inventoryValidator.isTodaySelected();
    }
    const fulfillmentConstants = window.FulfillmentConstants;
    const dateKey = fulfillmentConstants && fulfillmentConstants.DATE ? fulfillmentConstants.DATE : 'selected_fulfillment_date';
    const storageDate = localStorage.getItem(dateKey);
    if (!storageDate) return false;
    return !!window.FulfillmentDateUtils?.isSelectedDateToday?.(storageDate);
  }

  isFulfillmentReadyForReactiveUpdates() {
    const isCommitted = !!(window.FulfillmentState && typeof window.FulfillmentState.isCommitted === 'function' && window.FulfillmentState.isCommitted());
    const isDraftMode = !!(window.FulfillmentState && typeof window.FulfillmentState.isDraftMode === 'function' && window.FulfillmentState.isDraftMode());
    return isCommitted && !isDraftMode;
  }

  isTodayInventoryGuardActive() {
    if (!this.isFulfillmentReadyForReactiveUpdates()) {
      return false;
    }
    const locationId = this.getSelectedLocationIdFromStorage();
    if (!locationId) return false;
    return this.isSelectedFulfillmentDateToday();
  }

  getCachedInventoryProducts() {
    if (window.inventoryValidator && typeof window.inventoryValidator.getCachedInventoryData === 'function') {
      const locationId = typeof window.inventoryValidator.getSelectedLocationId === 'function'
        ? window.inventoryValidator.getSelectedLocationId()
        : null;
      const inventoryData = window.inventoryValidator.getCachedInventoryData(locationId);
      if (inventoryData && inventoryData.products) {
        return inventoryData.products;
      }
    }
    return null;
  }

  getToGoInventoryFromProduct(product) {
    if (!product || !product.variants) {
      return null;
    }

    for (const variantIdKey in product.variants) {
      const variant = product.variants[variantIdKey];
      if (variant && variant.inventory) {
        return {
          available: Math.max(0, Number(variant.inventory.available) || 0),
          committed: 0,
          incoming: Number(variant.inventory.incoming) || 0
        };
      }
    }

    return null;
  }

  getInventoryInfoForCartItem(item, productsData) {
    if (!item || !productsData) {
      return null;
    }

    const variantId = String(item.dataset.variantId || '');
    if (!variantId) {
      return null;
    }

    const isBundle = item.dataset.isBundle === 'true';
    const itemProductId = this.normalizeProductId(item.dataset.productId);

    if (isBundle && itemProductId && productsData[itemProductId]) {
      return {
        available: Math.max(0, Number(this.getBundleInventory(itemProductId, productsData)) || 0),
        committed: 0,
        incoming: 0
      };
    }

    for (const productId in productsData) {
      const product = productsData[productId];
      if (!product || !product.variants || !product.variants[variantId]) continue;

      const isPayItForward = product.collections?.some((collection) =>
        collection.handle === 'pay-it-forward-collection' ||
        collection.handle === 'pay-it-forward'
      );
      if (isPayItForward) {
        return { available: 999, committed: 0, incoming: 0 };
      }

      const toGo = this.getToGoInventoryFromProduct(product);
      if (toGo) return toGo;
    }

    if (itemProductId && productsData[itemProductId]) {
      const fallbackProduct = productsData[itemProductId];
      const isPayItForwardFallback = fallbackProduct.collections?.some((collection) =>
        collection.handle === 'pay-it-forward-collection' ||
        collection.handle === 'pay-it-forward'
      );
      if (isPayItForwardFallback) {
        return { available: 999, committed: 0, incoming: 0 };
      }
      const toGoFallback = this.getToGoInventoryFromProduct(fallbackProduct);
      if (toGoFallback) return toGoFallback;
      if (fallbackProduct.bundleComponents && Array.isArray(fallbackProduct.bundleComponents) && fallbackProduct.bundleComponents.length > 0) {
        return {
          available: Math.max(0, Number(this.getBundleInventory(itemProductId, productsData)) || 0),
          committed: 0,
          incoming: 0
        };
      }
    }

    return null;
  }

  getInventoryInfoForRecommendationElement(element, productsData) {
    if (!element || !productsData) return null;

    const productIdRaw = element.getAttribute('data-product-id') || '';
    const productIdNormalized = this.normalizeProductId(productIdRaw);
    let product =
      (productIdNormalized && productsData[productIdNormalized]) ||
      (productIdRaw && productsData[productIdRaw]);

    if (!product) {
      const variantIdRaw = element.getAttribute('data-variant-id') || '';
      if (variantIdRaw) {
        const normalizedVariantId =
          window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeEntityId === 'function'
            ? window.EtInventoryDomain.normalizeEntityId(variantIdRaw)
            : String(variantIdRaw).trim();

        for (const productId in productsData) {
          const candidate = productsData[productId];
          if (!candidate || !candidate.variants) continue;
          if (candidate.variants[variantIdRaw] || candidate.variants[normalizedVariantId]) {
            product = candidate;
            break;
          }
          for (const variantKey in candidate.variants) {
            const keyNorm =
              window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeEntityId === 'function'
                ? window.EtInventoryDomain.normalizeEntityId(variantKey)
                : String(variantKey).trim();
            if (keyNorm === normalizedVariantId) {
              product = candidate;
              break;
            }
          }
          if (product) break;
        }
      }
    }

    if (!product) return null;

    const isPayItForward = product.collections?.some((collection) =>
      collection.handle === 'pay-it-forward-collection' ||
      collection.handle === 'pay-it-forward'
    );
    if (isPayItForward) {
      return { available: 999, committed: 0, incoming: 0 };
    }

    if (product.bundleComponents && Array.isArray(product.bundleComponents) && product.bundleComponents.length > 0) {
      return {
        available: Math.max(0, Number(this.getBundleInventory(productIdNormalized || productIdRaw, productsData)) || 0),
        committed: 0,
        incoming: 0
      };
    }

    const toGo = this.getToGoInventoryFromProduct(product);
    return toGo;
  }

  // Check inventory before allowing increment
  async checkInventoryBeforeIncrement(index) {
    // Get the cart item element
    const item = Array.from(this.querySelectorAll('.cart-drawer__item'))[index - 1];
    if (!item) {
      console.error('❌ Cart Drawer: Could not find cart item with index:', index);
      return true; // Allow increment if we can't find the item
    }

    // Get variant ID from the item
    const variantId = item.dataset.variantId;
    if (!variantId) {
      return true; // Allow increment if no variant ID
    }



    // Get current quantity
    const quantityElement = item.querySelector('.cart-drawer__quantity-number');
    const currentQty = quantityElement ? parseInt(quantityElement.textContent) : 0;

    if (!this.isTodayInventoryGuardActive()) {
      return true;
    }

    const productsData = this.getCachedInventoryProducts();
    if (!productsData) {
      if (window.inventoryValidator && typeof window.inventoryValidator.ensureInventoryPrefetch === 'function') {
        window.inventoryValidator.ensureInventoryPrefetch().catch(() => {});
      }
      this.showInventoryWarning(item, 0);
      return false;
    }

    const inventoryInfo = this.getInventoryInfoForCartItem(item, productsData);
    if (!inventoryInfo) {
      this.showInventoryWarning(item, 0);
      return false;
    }

    const availableQuantity = Math.max(0, Number(inventoryInfo.available) || 0);
    if (currentQty >= availableQuantity) {
      this.showInventoryWarning(item, availableQuantity);
      return false; // Prevent increment
    }

    return true; // Allow increment
  }

  // Get bundle inventory based on component availability
  getBundleInventory(productId, productsData) {
    const product = productsData[productId];
    if (!product) {
      return 0;
    }

    // If bundle components are not defined in API response, return 0 to prevent overselling
    if (!product.bundleComponents || !Array.isArray(product.bundleComponents) || product.bundleComponents.length === 0) {
      return 0;
    }

    let minAvailable = Infinity;

    for (const component of product.bundleComponents) {
      const componentProductId = component.product_id;
      const componentQuantity = component.quantity || 1;

      const componentInventory = this.findComponentInventory(componentProductId, productsData);
      if (componentInventory) {
        const availableForBundle = Math.floor(componentInventory.available / componentQuantity);
        minAvailable = Math.min(minAvailable, availableForBundle);
      } else {
        minAvailable = 0;
      }
    }

    if (minAvailable === Infinity) {
      minAvailable = 0;
    }

    return minAvailable;
  }

  // Find component inventory (To-Go variant)
  findComponentInventory(productId, productsData) {
    const product = productsData[productId];
    if (!product || !product.variants) {
      return null;
    }

    // Find the To-Go variant for this component product
    for (const variantId in product.variants) {
      const variant = product.variants[variantId];
      if (variant && variant.inventory) {
        return {
          available: variant.inventory.available || 0,
          committed: 0,
          incoming: variant.inventory.incoming || 0
        };
      }
    }

    return null;
  }

  // Show inventory warning
  showInventoryWarning(item, availableQuantity) {
    
    // Create warning notification
    const notification = document.createElement('div');
    notification.className = 'cart-drawer-inventory-warning';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f8d7da;
      border: 1px solid #f5c6cb;
      border-radius: 4px;
      padding: 15px;
      max-width: 400px;
      z-index: 10001;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: #721c24;
    `;

    const productTitle = item.querySelector('.cart-drawer__item-title')?.textContent || 'Product';
    
    notification.innerHTML = `
      <strong>Inventory Limit Reached</strong><br><br>
      You've reached the maximum available quantity (${availableQuantity}) for ${productTitle}.<br><br>
      <small>No more items can be added to your cart for today's delivery.</small>
    `;

    // Add close button
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = `
      position: absolute;
      top: 5px;
      right: 10px;
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      color: #721c24;
    `;
    closeBtn.onclick = () => {
      notification.remove();
    };
    notification.appendChild(closeBtn);

    // Add to page
    document.body.appendChild(notification);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 5000);

    // Also disable the plus button temporarily
    const plusButton = item.querySelector('.cart-drawer__quantity-plus');
    if (plusButton) {
      plusButton.classList.add('disabled');
      plusButton.style.opacity = '0.5';
      plusButton.style.cursor = 'not-allowed';
      
      // Re-enable after 3 seconds
      setTimeout(() => {
        plusButton.classList.remove('disabled');
        plusButton.style.opacity = '';
        plusButton.style.cursor = '';
      }, 3000);
    }
  }

  showRecommendationInventoryWarning(button, availableQuantity) {
    
    // Get product title from the recommendation item
    const recommendationItem = button.closest('.cart-drawer__recommendation-item');
    const productTitle = recommendationItem?.querySelector('.cart-drawer__recommendation-title')?.textContent || 'Product';
    
    // Create warning notification
    const notification = document.createElement('div');
    notification.className = 'cart-drawer-recommendation-inventory-warning';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f8d7da;
      border: 1px solid #f5c6cb;
      border-radius: 4px;
      padding: 15px;
      max-width: 400px;
      z-index: 10001;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: #721c24;
      animation: slideInRight 0.3s ease-out;
    `;

    notification.innerHTML = `
      <strong>⚠️ Out of Stock</strong><br><br>
      ${productTitle} is currently out of stock for today's delivery.<br><br>
      <small>Please try again tomorrow or select a different item.</small>
    `;

    // Add close button
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = `
      position: absolute;
      top: 5px;
      right: 10px;
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      color: #721c24;
    `;
    closeBtn.onclick = () => {
      notification.style.animation = 'slideOutRight 0.3s ease-in';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 300);
    };
    notification.appendChild(closeBtn);

    // Add to page
    document.body.appendChild(notification);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = 'slideOutRight 0.3s ease-in';
        setTimeout(() => {
          if (notification.parentNode) {
            notification.remove();
          }
        }, 300);
      }
    }, 5000);

    // Also temporarily disable the add button
    button.classList.add('disabled');
    button.style.opacity = '0.5';
    button.style.cursor = 'not-allowed';
    
    // Re-enable after 3 seconds
    setTimeout(() => {
      button.classList.remove('disabled');
      button.style.opacity = '';
      button.style.cursor = '';
    }, 3000);
  }

  // Actualizamos la función para reflejar que el botón menos ahora elimina productos cuando la cantidad es 1
  updateButtonStates() {
    const items = this.querySelectorAll('.cart-drawer__item');
    
    items.forEach((item, index) => {
      const qtyElement = item.querySelector('.cart-drawer__quantity-number');
      const minusButton = item.querySelector('.cart-drawer__quantity-minus');
      const plusButton = item.querySelector('.cart-drawer__quantity-plus');
      
      if (qtyElement && minusButton) {
        // Eliminamos la lógica que deshabilitaba el botón cuando qty === 1
        // ya que ahora queremos que elimine el producto
        minusButton.classList.remove('disabled');
        
        // Si necesitamos dar un aspecto visual distinto cuando qty === 1 para indicar
        // que el siguiente clic eliminará el producto, podríamos añadirlo aquí
        const qty = parseInt(qtyElement.textContent);
        if (qty === 1) {
          minusButton.setAttribute('title', 'Eliminar producto');
          minusButton.classList.add('will-remove');
        } else {
          minusButton.removeAttribute('title');
          minusButton.classList.remove('will-remove');
        }
      }

      // Check inventory for plus button
      if (qtyElement && plusButton) {
        const currentQty = parseInt(qtyElement.textContent);

        if (item.dataset.isBundle === 'true') {
          plusButton.classList.remove('disabled');
          plusButton.style.opacity = '';
          plusButton.style.cursor = '';
          plusButton.removeAttribute('title');
          return;
        }

        // Non-today flow: quantity controls should not be inventory-locked.
        if (!this.isTodayInventoryGuardActive()) {
          plusButton.classList.remove('disabled');
          plusButton.style.opacity = '';
          plusButton.style.cursor = '';
          plusButton.removeAttribute('title');
          return;
        }

        const productsData = this.getCachedInventoryProducts();
        const inventoryInfo = productsData ? this.getInventoryInfoForCartItem(item, productsData) : null;
        if (!inventoryInfo) {
          plusButton.classList.add('disabled');
          plusButton.style.opacity = '0.5';
          plusButton.style.cursor = 'not-allowed';
          plusButton.setAttribute('title', 'Checking availability...');
          return;
        }

        const availableQuantity = Math.max(0, Number(inventoryInfo.available) || 0);
        if (currentQty >= availableQuantity) {
          plusButton.classList.add('disabled');
          plusButton.style.opacity = '0.5';
          plusButton.style.cursor = 'not-allowed';
          plusButton.setAttribute('title', `Maximum available: ${availableQuantity}`);
        } else {
          plusButton.classList.remove('disabled');
          plusButton.style.opacity = '';
          plusButton.style.cursor = '';
          plusButton.removeAttribute('title');
        }
      }
    });
  }

    // New method to render recommendations with inventory filtering
  async renderRecommendationsWithInventory() {
    // Check if inventory check has already been run for this drawer session
    if (this.inventoryCheckRun) {
      return;
    }
    
    const recommendationsContainer = this.querySelector('.cart-drawer__recommendations-items');
    if (!recommendationsContainer) {
      return;
    }

    // Get all recommendation items
    const recommendationItems = recommendationsContainer.querySelectorAll('.cart-drawer__recommendation-item');

    // First, hide all items by default to prevent flashing
    recommendationItems.forEach((item) => {
      item.style.opacity = '0';
      item.style.transform = 'translateY(10px)';
      item.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
    });

    if (!this.isFulfillmentReadyForReactiveUpdates()) {
      this.resetRecommendationsToCatalogState();
      return;
    }

    const selectedLocationId = this.getSelectedLocationIdFromStorage();
    if (!this.isTodayInventoryGuardActive() || !selectedLocationId) {
      this.resetRecommendationsToCatalogState();
      return;
    }

    if (!window.inventoryValidator) {
      this.resetRecommendationsToCatalogState();
      return;
    }

    // Get cached inventory data from the same source used by menu inventory checks
    const cachedData = { data: window.inventoryValidator.getCachedInventoryData(selectedLocationId) };
    if (!cachedData || !cachedData.data || !cachedData.data.products) {
      if (
        window.inventoryValidator &&
        typeof window.inventoryValidator.ensureInventoryPrefetch === 'function' &&
        selectedLocationId &&
        !this._recommendationsPrefetchPending
      ) {
        this._recommendationsPrefetchPending = true;
        Promise.resolve(window.inventoryValidator.ensureInventoryPrefetch(selectedLocationId))
          .then(() => {
            this._recommendationsPrefetchPending = false;
            this.inventoryCheckRun = false;
            if (this.classList.contains('active')) {
              this.renderRecommendationsWithInventory();
            }
          })
          .catch(() => {
            this._recommendationsPrefetchPending = false;
            this.resetRecommendationsToCatalogState();
          });
        return;
      }
      return;
    }

    // Set flag only when async work actually begins; cleared in finally + safety timeout
    this.inventoryCheckRun = true;
    const SAFETY_MS = 8000;
    const safetyTimer = setTimeout(() => {
      this.inventoryCheckRun = false;
    }, SAFETY_MS);

    try {
      await this.applyInventoryAndCatalogFiltering(cachedData);
    } finally {
      clearTimeout(safetyTimer);
      this.inventoryCheckRun = false;
    }
  }

  // New method to apply catalog-based filtering
  applyCatalogFiltering() {
    if (this.isB2BCustomer()) {
      this.showFirstRecommendationVariant();
      return;
    }

    const selectedCatalogId = localStorage.getItem('selected_catalog_id');

    if (!selectedCatalogId) {
      this.showAllRecommendations();
      return;
    }

    // Group items by product ID to ensure we only show one variant per product
    const productGroups = {};
    
    const recommendationItems = this.querySelectorAll('.cart-drawer__recommendation-item');
    recommendationItems.forEach((item) => {
      const productId = item.getAttribute('data-product-id');
      const catalogId = item.getAttribute('data-catalog-id');
      
      if (!productGroups[productId]) {
        productGroups[productId] = [];
      }
      
      productGroups[productId].push({
        element: item,
        catalogId: catalogId
      });
    });

    let visibleCount = 0;

    // For each product, show only the variant that matches the selected catalog
    Object.values(productGroups).forEach((variants, groupIndex) => {
      let hasVisibleVariant = false;
      
      variants.forEach((variant) => {
        const element = variant.element;
        const catalogId = variant.catalogId;
        
        if (catalogId === selectedCatalogId && !hasVisibleVariant) {
          // Show this variant with animation
          element.style.display = 'block';
          setTimeout(() => {
            element.style.opacity = '1';
            element.style.transform = 'translateY(0)';
          }, groupIndex * 100); // Stagger the animations by product group
          this.resetRecommendationButtonState(element.querySelector('.cart-drawer__add-recommendation'));
          hasVisibleVariant = true;
          visibleCount++;
        } else {
          // Hide this variant
          element.style.display = 'none';
          element.style.opacity = '0';
          element.style.transform = 'translateY(10px)';
        }
      });
    });

    this.handleEmptyRecommendations(visibleCount);
  }

  resetAllRecommendationButtons() {
    const recommendationButtons = this.querySelectorAll('.cart-drawer__add-recommendation');
    recommendationButtons.forEach((button) => {
      this.resetRecommendationButtonState(button);
    });
  }

  resetRecommendationsToCatalogState() {
    this.resetAllRecommendationButtons();
    this.applyCatalogFiltering();
  }

  isB2BCustomer() {
    return document.body && document.body.getAttribute('data-b2b-customer') === 'true';
  }

  getRecommendationProductGroups() {
    const productGroups = {};
    const recommendationItems = this.querySelectorAll('.cart-drawer__recommendation-item');
    recommendationItems.forEach((item) => {
      const productId = item.getAttribute('data-product-id');
      if (!productGroups[productId]) {
        productGroups[productId] = [];
      }
      productGroups[productId].push(item);
    });
    return Object.values(productGroups);
  }

  showFirstRecommendationVariant() {
    const productGroups = this.getRecommendationProductGroups();
    let visibleCount = 0;

    productGroups.forEach((variants, groupIndex) => {
      variants.forEach((element, variantIndex) => {
        if (variantIndex === 0) {
          element.style.display = 'block';
          setTimeout(() => {
            element.style.opacity = '1';
            element.style.transform = 'translateY(0)';
          }, groupIndex * 100);
          this.resetRecommendationButtonState(element.querySelector('.cart-drawer__add-recommendation'));
          visibleCount++;
        } else {
          element.style.display = 'none';
          element.style.opacity = '0';
          element.style.transform = 'translateY(10px)';
        }
      });
    });

    this.handleEmptyRecommendations(visibleCount);
  }

  async applyInventoryFilteringB2B(cachedData) {
    const productGroups = this.getRecommendationProductGroups();
    let visibleCount = 0;
    let cartSnapshot = null;

    if (window.cartWatcher) {
      try {
        if (typeof window.cartWatcher.storedCart === 'function') {
          const watcherCart = window.cartWatcher.storedCart();
          if (watcherCart && Array.isArray(watcherCart.items)) {
            cartSnapshot = watcherCart;
          }
        }
      } catch (error) {}
    }

    if ((!cartSnapshot || !Array.isArray(cartSnapshot.items)) && window.cartWatcher) {
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (response.ok) {
          const freshCart = await response.json();
          if (freshCart && Array.isArray(freshCart.items)) {
            cartSnapshot = freshCart;
          }
        }
      } catch (error) {
      }
    }

    for (let groupIndex = 0; groupIndex < productGroups.length; groupIndex++) {
      const variants = productGroups[groupIndex];
      if (!variants || variants.length === 0) continue;

      const preferredVariantElement = variants[0];

      variants.forEach((element, variantIndex) => {
        if (variantIndex > 0) {
          element.style.display = 'none';
          element.style.opacity = '0';
          element.style.transform = 'translateY(10px)';
        }
      });

      const variantId = preferredVariantElement.getAttribute('data-variant-id');
      const addButton = preferredVariantElement.querySelector('.cart-drawer__add-recommendation');
      const inventoryInfo = this.getInventoryInfoForRecommendationElement(preferredVariantElement, cachedData.data.products);
      const availableQuantity = inventoryInfo ? Math.max(0, Number(inventoryInfo.available) || 0) : 0;

      let currentCartQuantity = 0;
      if (cartSnapshot && Array.isArray(cartSnapshot.items) && variantId != null && String(variantId) !== '') {
        const cartItem = cartSnapshot.items.find((item) => {
          const vid = item && item.variant_id;
          if (vid == null) return false;
          return String(vid) === String(variantId);
        });
        currentCartQuantity = cartItem ? cartItem.quantity : 0;
      }

      if (availableQuantity <= 0) {
        preferredVariantElement.style.display = 'none';
        preferredVariantElement.style.opacity = '0';
        preferredVariantElement.style.transform = 'translateY(10px)';
        if (addButton) {
          addButton.disabled = true;
          addButton.classList.add('disabled');
          addButton.style.opacity = '0.5';
          addButton.style.cursor = 'not-allowed';
          addButton.setAttribute('title', 'Out of stock');
        }
        continue;
      }

      preferredVariantElement.style.display = 'block';
      setTimeout(() => {
        preferredVariantElement.style.opacity = '1';
        preferredVariantElement.style.transform = 'translateY(0)';
      }, groupIndex * 100);
      visibleCount++;

      if (addButton) {
        const wouldExceedInventory = (currentCartQuantity + 1) > availableQuantity;
        if (!wouldExceedInventory) {
          addButton.disabled = false;
          addButton.classList.remove('disabled');
          addButton.style.opacity = '1';
          addButton.style.cursor = 'pointer';
          addButton.removeAttribute('title');
        } else {
          addButton.disabled = true;
          addButton.classList.add('disabled');
          addButton.style.opacity = '0.5';
          addButton.style.cursor = 'not-allowed';
          addButton.setAttribute('title', `Already have ${currentCartQuantity} in cart (max: ${availableQuantity})`);
        }
      }
    }

    this.handleEmptyRecommendations(visibleCount);
  }

  // New method to apply both inventory and catalog filtering
  async applyInventoryAndCatalogFiltering(cachedData) {
    if (this.isB2BCustomer()) {
      await this.applyInventoryFilteringB2B(cachedData);
      return;
    }

    const selectedCatalogId = localStorage.getItem('selected_catalog_id');
    if (!selectedCatalogId) {
      await this.applyInventoryFilteringOnly(cachedData);
      return;
    }

    // Group items by product ID
    const productGroups = {};
    
    const recommendationItems = this.querySelectorAll('.cart-drawer__recommendation-item');
    recommendationItems.forEach((item) => {
      const productId = item.getAttribute('data-product-id');
      const catalogId = item.getAttribute('data-catalog-id');
      
      if (!productGroups[productId]) {
        productGroups[productId] = [];
      }
      
      productGroups[productId].push({
        element: item,
        catalogId: catalogId
      });
    });

    let visibleCount = 0;
    let cartSnapshot = null;

    if (window.cartWatcher) {
      try {
        if (typeof window.cartWatcher.storedCart === 'function') {
          const watcherCart = window.cartWatcher.storedCart();
          if (watcherCart && Array.isArray(watcherCart.items)) {
            cartSnapshot = watcherCart;
          }
        }
      } catch (error) {}
    }

    if ((!cartSnapshot || !Array.isArray(cartSnapshot.items)) && window.cartWatcher) {
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (response.ok) {
          const freshCart = await response.json();
          if (freshCart && Array.isArray(freshCart.items)) {
            cartSnapshot = freshCart;
          }
        }
      } catch (error) {
      }
    }

    const groups = Object.values(productGroups);

    // For each product, check inventory and show the matching catalog variant
    for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
      const variants = groups[groupIndex];
      let hasVisibleVariant = false;

      for (const variant of variants) {
        const element = variant.element;
        const catalogId = variant.catalogId;
        const variantId = element.getAttribute('data-variant-id');

        // Check if this variant matches the selected catalog
        if (catalogId === selectedCatalogId && !hasVisibleVariant) {
          // Check inventory for this variant
            const inventoryInfo = this.getInventoryInfoForRecommendationElement(element, cachedData.data.products);
            const availableQuantity = inventoryInfo ? Math.max(0, Number(inventoryInfo.available) || 0) : 0;

          // Get current cart quantity for this variant from a shared snapshot
          let currentCartQuantity = 0;
          if (cartSnapshot && Array.isArray(cartSnapshot.items) && variantId != null && String(variantId) !== '') {
            const cartItem = cartSnapshot.items.find((item) => {
              const vid = item && item.variant_id;
              if (vid == null) return false;
              return String(vid) === String(variantId);
            });
            currentCartQuantity = cartItem ? cartItem.quantity : 0;
          }

          if (availableQuantity <= 0) {
            element.style.display = 'none';
            element.style.opacity = '0';
            element.style.transform = 'translateY(10px)';
            const addButton = element.querySelector('.cart-drawer__add-recommendation');
            if (addButton) {
              addButton.disabled = true;
              addButton.classList.add('disabled');
              addButton.style.opacity = '0.5';
              addButton.style.cursor = 'not-allowed';
              addButton.setAttribute('title', 'Out of stock');
            }
            continue;
          }

          element.style.display = 'block';
          setTimeout(() => {
            element.style.opacity = '1';
            element.style.transform = 'translateY(0)';
          }, groupIndex * 100);
          hasVisibleVariant = true;
          visibleCount++;

          // Update add button state based on inventory and current cart quantity
          const addButton = element.querySelector('.cart-drawer__add-recommendation');
          if (addButton) {
            const wouldExceedInventory = (currentCartQuantity + 1) > availableQuantity;
            if (!wouldExceedInventory) {
              addButton.disabled = false;
              addButton.classList.remove('disabled');
              addButton.style.opacity = '1';
              addButton.style.cursor = 'pointer';
              addButton.removeAttribute('title');
            } else {
              // Disable button if no inventory or would exceed inventory limits
              addButton.disabled = true;
              addButton.classList.add('disabled');
              addButton.style.opacity = '0.5';
              addButton.style.cursor = 'not-allowed';
              addButton.setAttribute('title', `Already have ${currentCartQuantity} in cart (max: ${availableQuantity})`);
            }
          }
        } else {
          // Hide this variant
          element.style.display = 'none';
          element.style.opacity = '0';
          element.style.transform = 'translateY(10px)';
        }
      }
    }

    this.handleEmptyRecommendations(visibleCount);
  }

  // Helper method to show all recommendations (fallback)
  showAllRecommendations() {
    const recommendationItems = this.querySelectorAll('.cart-drawer__recommendation-item');
    recommendationItems.forEach((item, index) => {
      item.style.display = 'block';
      setTimeout(() => {
        item.style.opacity = '1';
        item.style.transform = 'translateY(0)';
      }, index * 50);
      this.resetRecommendationButtonState(item.querySelector('.cart-drawer__add-recommendation'));
    });
  }

  resetRecommendationButtonState(addButton) {
    if (!addButton) return;
    addButton.disabled = false;
    addButton.classList.remove('disabled', 'loading', 'error', 'success');
    addButton.style.opacity = '1';
    addButton.style.cursor = 'pointer';
    addButton.removeAttribute('title');
    if (addButton.querySelector('.cart-drawer__add-recommendation-icon')) {
      addButton.innerHTML = '<span class="cart-drawer__add-recommendation-icon">+</span>';
    }
  }

  // Helper method to handle empty recommendations state
  handleEmptyRecommendations(visibleCount) {
    const recommendationsSection = this.querySelector('.cart-drawer__recommendations');
    if (recommendationsSection && visibleCount === 0) {
      const emptyMessage = recommendationsSection.querySelector('.cart-drawer__recommendations-empty');
      if (!emptyMessage) {
        const message = document.createElement('p');
        message.className = 'cart-drawer__recommendations-empty';
        message.textContent = 'No products available for today\'s delivery.';
        message.style.opacity = '0';
        message.style.transform = 'translateY(10px)';
        message.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        recommendationsSection.appendChild(message);
        
        setTimeout(() => {
          message.style.opacity = '1';
          message.style.transform = 'translateY(0)';
        }, 100);
      }
    } else if (recommendationsSection) {
      const emptyMessage = recommendationsSection.querySelector('.cart-drawer__recommendations-empty');
      if (emptyMessage) {
        emptyMessage.remove();
      }
    }
  }

  // Helper method to apply inventory filtering only (when no catalog ID is selected)
  async applyInventoryFilteringOnly(cachedData) {
    if (this.isB2BCustomer()) {
      await this.applyInventoryFilteringB2B(cachedData);
      return;
    }

    const recommendationItems = this.querySelectorAll('.cart-drawer__recommendation-item');
    let visibleCount = 0;

    let cartSnapshot = null;
    if (window.cartWatcher) {
      try {
        if (typeof window.cartWatcher.storedCart === 'function') {
          const watcherCart = window.cartWatcher.storedCart();
          if (watcherCart && Array.isArray(watcherCart.items)) {
            cartSnapshot = watcherCart;
          }
        }
      } catch (error) {}
    }

    if ((!cartSnapshot || !Array.isArray(cartSnapshot.items)) && window.cartWatcher) {
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (response.ok) {
          const freshCart = await response.json();
          if (freshCart && Array.isArray(freshCart.items)) {
            cartSnapshot = freshCart;
          }
        }
      } catch (error) {
      }
    }

    for (let index = 0; index < recommendationItems.length; index++) {
      const item = recommendationItems[index];
      const variantId = item.getAttribute('data-variant-id');
      const addButton = item.querySelector('.cart-drawer__add-recommendation');
      
      if (!variantId) {
        continue;
      }

      // Find inventory for this variant
      const inventoryInfo = this.getInventoryInfoForRecommendationElement(item, cachedData.data.products);
      const availableQuantity = inventoryInfo ? Math.max(0, Number(inventoryInfo.available) || 0) : 0;

      // Get current cart quantity for this variant from a shared snapshot
      let currentCartQuantity = 0;
      if (cartSnapshot && Array.isArray(cartSnapshot.items) && variantId != null && String(variantId) !== '') {
        const cartItem = cartSnapshot.items.find((item) => {
          const vid = item && item.variant_id;
          if (vid == null) return false;
          return String(vid) === String(variantId);
        });
        currentCartQuantity = cartItem ? cartItem.quantity : 0;
      }

      if (availableQuantity <= 0) {
        item.style.display = 'none';
        item.style.opacity = '0';
        item.style.transform = 'translateY(10px)';
        if (addButton) {
          addButton.disabled = true;
          addButton.classList.add('disabled');
          addButton.style.opacity = '0.5';
          addButton.style.cursor = 'not-allowed';
          addButton.setAttribute('title', 'Out of stock');
        }
        continue;
      }

      item.style.display = 'block';
      setTimeout(() => {
        item.style.opacity = '1';
        item.style.transform = 'translateY(0)';
      }, index * 50);
      visibleCount++;
      
      // Update add button state based on inventory and current cart quantity
      if (addButton) {
        const wouldExceedInventory = (currentCartQuantity + 1) > availableQuantity;
        if (!wouldExceedInventory) {
          addButton.disabled = false;
          addButton.classList.remove('disabled');
          addButton.style.opacity = '1';
          addButton.style.cursor = 'pointer';
          addButton.removeAttribute('title');
        } else {
          addButton.disabled = true;
          addButton.classList.add('disabled');
          addButton.style.opacity = '0.5';
          addButton.style.cursor = 'not-allowed';
          addButton.setAttribute('title', `Already have ${currentCartQuantity} in cart (max: ${availableQuantity})`);
        }
      }
    }

    this.handleEmptyRecommendations(visibleCount);
  }
  
  // Public method to refresh recommendations (can be called from external systems)
  refreshRecommendations() {
    if (this.classList.contains('active')) {
      // Reset the flag to allow inventory check to run again
      this.inventoryCheckRun = false;
      setTimeout(async () => {
        await this.renderRecommendationsWithInventory();
      }, 100);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

(function () {
  const CartItemsBase = window.CartItems;
  if (typeof CartItemsBase !== 'function') {
    return;
  }

  class CartDrawerItems extends CartItemsBase {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
})();

// Global function to refresh cart drawer recommendations
window.refreshCartDrawerRecommendations = function() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.refreshRecommendations === 'function') {
    cartDrawer.refreshRecommendations();
  }
};
